import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package } from 'lucide-react';
import { useLocation } from '../contexts/LocationContext';
import Header from '../components/Header';
import ProductCard from '../components/ProductCard';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CategoryProductsPage() {
    const { categoryName } = useParams();
    const navigate = useNavigate();
    const { location } = useLocation();

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [displayedProducts, setDisplayedProducts] = useState([]);
    const [showCount, setShowCount] = useState(20); // Show 20 products initially

    useEffect(() => {
        const fetchCategoryProducts = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await axios.get(`${API}/products/category/${encodeURIComponent(categoryName)}`, {
                    params: {
                        lon: location.lon,
                        lat: location.lat,
                        page: 1,
                        max_pages: 5 // Reduced from 10 for better performance
                    }
                });

                if (response.data.status === 'success') {
                    const allProducts = response.data.data || [];
                    setProducts(allProducts);
                    setDisplayedProducts(allProducts.slice(0, showCount));
                } else {
                    setError('Gagal memuat produk');
                }
            } catch (err) {
                console.error('Error fetching category products:', err);
                setError('Terjadi kesalahan saat memuat produk');
                toast.error('Gagal memuat produk');
            } finally {
                setLoading(false);
            }
        };

        fetchCategoryProducts();
    }, [categoryName, location]);

    useEffect(() => {
        // Update displayed products when showCount changes
        setDisplayedProducts(products.slice(0, showCount));
    }, [showCount, products]);

    const handleLoadMore = () => {
        setShowCount(prev => prev + 20);
    };

    const handleBack = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8F9FA]">
                <Header />
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {[...Array(10)].map((_, i) => (
                                <div key={i} className="h-64 bg-gray-200 rounded-xl"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#F8F9FA]">
                <Header />
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                    <Button
                        variant="ghost"
                        onClick={handleBack}
                        className="mb-6"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Kembali
                    </Button>
                    <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Package className="w-12 h-12 text-gray-400" />
                        </div>
                        <h3 className="font-medium text-gray-900 mb-1">{error}</h3>
                        <p className="text-sm text-gray-500 mb-4">Silakan kembali ke halaman utama</p>
                        <Button onClick={handleBack} className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                            Kembali ke Beranda
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F9FA]">
            <Header />

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                {/* Back Button */}
                <Button
                    variant="ghost"
                    onClick={handleBack}
                    className="mb-6 hover:bg-gray-100"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Kembali ke Beranda
                </Button>

                {/* Category Header */}
                <div className="mb-8">
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                        <Package className="w-8 h-8 text-[#0052CC]" />
                        {categoryName}
                    </h1>
                    <p className="text-gray-600">
                        Menampilkan {displayedProducts.length} dari {products.length} produk
                    </p>
                </div>

                {/* Products Grid */}
                {displayedProducts.length > 0 ? (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {displayedProducts.map((product, index) => (
                                <ProductCard
                                    key={`${product.product_id}-${index}`}
                                    product={product}
                                    merchant={product.merchant}
                                />
                            ))}
                        </div>

                        {/* Load More Button */}
                        {displayedProducts.length < products.length && (
                            <div className="mt-8 text-center">
                                <Button
                                    onClick={handleLoadMore}
                                    className="bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-full px-8 py-3"
                                >
                                    Muat Lebih Banyak ({products.length - displayedProducts.length} produk lagi)
                                </Button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Package className="w-12 h-12 text-gray-400" />
                        </div>
                        <h3 className="font-medium text-gray-900 mb-1">Tidak ada produk</h3>
                        <p className="text-sm text-gray-500">Kategori ini belum memiliki produk</p>
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
}
